/**
 * components/ui/AppTabs.js
 * ----------------------------------------------------------------------------
 * The section tabs at the top of a page (the wallet page's Money In,
 * Disbursements, Payout Batches ...), with the page's own actions beside them.
 *
 * WHY A COMPONENT (1 Nov 2026). The wallet page drew its nine tabs as a
 * wrapping row of pills. On a phone that became five ragged rows of uneven
 * widths, with Rider Batch or Run Reconciliation squeezed in among them (Izi,
 * 1 Nov 2026). The layout now changes with the screen, in CSS only:
 *
 *   wide    one row of pills, the actions pushed to the right.
 *   phone   (below 576px) an even grid, three across by default, icon over
 *           label, every tile the same size, so every section stays in view
 *           without sideways scrolling; the actions get a full-width row
 *           underneath; a count badge sits in the tile's corner.
 *
 * The chosen tab is filled in the action navy; the others are the light tint,
 * so nothing is outlined.
 *
 *   <AppTabs
 *     ariaLabel="Wallet sections"
 *     value={activeTab}
 *     onChange={setActiveTab}
 *     tabs={[{ id: "moneyIn", label: "Money In", icon: <FiDownload />, badge: 3 }]}
 *     actions={activeTab === "batches" ? <button ...>Rider Batch</button> : null}
 *   />
 *
 *   compactColumns   tiles per row on a phone (default 3)
 */
import React from "react";
import "./ui.css";

const AppTabs = ({ tabs = [], value, onChange, ariaLabel, actions, compactColumns = 3, className = "" }) => (
  <div className={`lk-tabs ${className}`.trim()}>
    <div
      className="lk-tabs__list"
      role="tablist"
      aria-label={ariaLabel}
      style={{ "--lk-tabs-cols": compactColumns }}
    >
      {tabs.map((t) => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`lk-tabs__tab${active ? " is-active" : ""}`}
            onClick={() => onChange?.(t.id)}
          >
            {t.icon ? <span className="lk-tabs__icon" aria-hidden="true">{t.icon}</span> : null}
            <span className="lk-tabs__label">{t.label}</span>
            {t.badge ? <span className="lk-tabs__badge">{t.badge}</span> : null}
          </button>
        );
      })}
    </div>
    {actions ? <div className="lk-tabs__actions">{actions}</div> : null}
  </div>
);

export default AppTabs;
